import React, {
  useEffect,
  useRef,
  useState,
  useImperativeHandle,
  forwardRef
} from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import Button from '../../../../_shared/components/Button';
import HelpModal from '../../HelpModal';
import MixpanelService from '../../../../services/MixpanelService';
import MixpanelConfig from '../../../../_shared/constants/mixpanelConfig';
import {
  FilterContainer,
  FilterHeader,
  FilterHeaderIcon,
  FilterPicker,
  FilterHandle,
  StyledSection,
  StyledLabel,
  StyledInfo,
  NoTradeInHeader
} from './Styles';
import { VEHICLE_TYPE } from '../../../../_shared/constants/retail';

const propTypes = {
  updateFilter: PropTypes.func.isRequired,
  isMobile: PropTypes.bool.isRequired
};

const Filter = forwardRef(({ updateFilter, isMobile }, ref) => {
  const [hasTradeInVehicle, setHasTradeInVehicle] = useState(true);
  const mixpanel = MixpanelService.get();
  const [vehicleType, setVehicleType] = useState({
    new: true,
    used: false,
    notSure: false
  });
  const filterHandleRef = useRef(null);
  const filterRef = useRef(null);
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);

  const getVehicleType = () => {
    const keys = Object.keys(vehicleType);

    return keys.find(key => vehicleType[key]);
  };

  useImperativeHandle(ref, () => ({
    dismiss: () => {
      if (filterRef?.current && isMobile) {
        filterRef.current.style.bottom = `-211px`;
      }
    }
  }));

  const updateVehicleType = type => {
    switch (type) {
      case VEHICLE_TYPE.new:
        setVehicleType({ new: true, used: false, notSure: false });
        updateFilter(currentState => {
          return {
            ...currentState,
            isFranchiseDealer: true
          };
        });
        break;
      case VEHICLE_TYPE.used:
        setVehicleType({ new: false, used: true, notSure: false });
        updateFilter(currentState => {
          return {
            ...currentState,
            isFranchiseDealer: false
          };
        });
        break;
      case VEHICLE_TYPE.notsure:
        setVehicleType({ new: false, used: false, notSure: true });
        updateFilter(currentState => {
          return {
            ...currentState,
            isFranchiseDealer: false
          };
        });
        break;
      default:
    }
  };

  const updateHasTradeIn = hasTradeIn => {
    setHasTradeInVehicle(hasTradeIn);
    mixpanel.track(MixpanelConfig.names.tiahFilterDealers, {
      have_tradein_vehicle: hasTradeIn
    });
    updateFilter(currentState => {
      return {
        ...currentState,
        hasTradeIn
      };
    });
  };

  const openHelpModal = () => {
    mixpanel.track(MixpanelConfig.names.tiahFilterDealers, {
      how_to_pick_right_dealer: true
    });
    setIsHelpModalOpen(true);
  };

  useEffect(() => {
    mixpanel.track(MixpanelConfig.names.tiahFilterDealers, {
      have_tradein_vehicle: hasTradeInVehicle,
      type_of_vehicle:
        getVehicleType() === 'notSure' ? 'not sure' : getVehicleType()
    });
  });

  useEffect(() => {
    const endPosition = -211;

    let clickedAtY;

    const filterHandleRefCopy = filterHandleRef?.current;

    if (!isMobile) {
      return undefined;
    }

    /**
     * Handles event for touchscreen move on filter window
     * allows dragging/collapsing filter window
     */
    function moveHandler(e) {
      const posY = e.touches[0].pageY - clickedAtY;

      const posfinal = Math.max(-posY, endPosition);

      setTimeout(() => {
        filterRef.current.style.bottom = `${posfinal > 0 ? 0 : -211}px`;
      }, 100);
    }

    function stopHandler() {
      document.removeEventListener('touchmove', moveHandler);
      document.removeEventListener('touchend', stopHandler);
    }

    function handleTouchStart(e) {
      clickedAtY = e.touches[0].pageY;

      document.addEventListener('touchmove', moveHandler);
      document.addEventListener('touchend', stopHandler);
    }

    filterHandleRefCopy?.addEventListener('touchstart', handleTouchStart);

    return () =>
      filterHandleRefCopy?.removeEventListener('touchstart', handleTouchStart);
  }, [isMobile]);

  return (
    <FilterContainer ref={filterRef} hasTradeInVehicle={hasTradeInVehicle}>
      <div ref={filterHandleRef}>
        {isMobile && <FilterHandle />}
        <FilterHeader>
          <FilterHeaderIcon />
          <FormattedMessage id="tiah.map.filter.title" />
        </FilterHeader>
      </div>
      <StyledSection>
        <StyledLabel>
          <FormattedMessage id="tiah.map.filter.question1" />
        </StyledLabel>
        <FilterPicker>
          <Button.Controlled
            variant={hasTradeInVehicle}
            onClick={() => updateHasTradeIn(true)}
          >
            <FormattedMessage id="tiah.map.filter.yes" />
          </Button.Controlled>
          <Button.Controlled
            variant={!hasTradeInVehicle}
            onClick={() => updateHasTradeIn(false)}
          >
            <FormattedMessage id="tiah.map.filter.no" />
          </Button.Controlled>
        </FilterPicker>
      </StyledSection>

      {hasTradeInVehicle && (
        <>
          <StyledSection>
            <StyledLabel>
              <FormattedMessage id="tiah.map.filter.question2" />
            </StyledLabel>
            <FilterPicker>
              <Button.Controlled
                variant={vehicleType.new}
                onClick={() => updateVehicleType(VEHICLE_TYPE.new)}
              >
                <FormattedMessage id="tiah.map.filter.new" />
              </Button.Controlled>
              <Button.Controlled
                variant={vehicleType.used}
                onClick={() => updateVehicleType(VEHICLE_TYPE.used)}
              >
                <FormattedMessage id="tiah.map.filter.used" />
              </Button.Controlled>
              <Button.Controlled
                variant={vehicleType.notSure}
                onClick={() => updateVehicleType(VEHICLE_TYPE.notsure)}
              >
                <FormattedMessage id="tiah.map.filter.notsure" />
              </Button.Controlled>
            </FilterPicker>
          </StyledSection>

          <StyledSection>
            <StyledInfo onClick={() => openHelpModal()}>
              <FormattedMessage id="tiah.map.filter.help" />
            </StyledInfo>
          </StyledSection>
        </>
      )}

      {!hasTradeInVehicle && (
        <StyledSection>
          <NoTradeInHeader>
            <FormattedMessage id="tiah.map.filter.notradein.title" />
          </NoTradeInHeader>
          <FormattedMessage id="tiah.map.filter.notradein.message" />
        </StyledSection>
      )}

      <HelpModal
        isOpen={isHelpModalOpen}
        closeModal={() => setIsHelpModalOpen(false)}
      />
    </FilterContainer>
  );
});

Filter.propTypes = propTypes;

export default Filter;
